import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { IconSizes, IconType } from '@nibtravel/shared/ui/components/icon';
import { IconPosition } from '@nibtravel/shared/ui/components/link-icon';
import { Option } from '@nibtravel/shared/util/e2e';
import {
  fadeInOnEnterAnimation,
  fadeOutUpOnLeaveAnimation,
} from 'angular-animations';
import { BehaviorSubject } from 'rxjs';

/**
 * Generic option component to abstract common ui elements between all plan options.
 * To modify the price, subscribe to the component responsible to fetch the data,
 * such as a dropdown, checkbox or radio button. Perform the necessary logic
 * e.g.: dispatch a state action to fetch price and update the property optionDetailPrice
 *
 * @example
 * <nibtravel-option
 *    [title]="'Snow Sports"
 *    [leadText]="'Would you like to include coverage for snow sports?"
 *    [optionDetailPrice]="optionDetailPrice"
 *    [optionDetailLabel]="optionDetailLabel"
 *    (optionRemoved)="onOptionRemoved($event)">
 *   <nibtravel-checkbox (checked)="onOptionChecked($event)"></nibtravel-checkbox>
 * </nibtravel-option>
 *
 * @export
 * @class OptionComponent
 */
@Component({
  selector: 'nibtravel-option',
  templateUrl: './option.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeInOnEnterAnimation({ anchor: 'enter' }),
    fadeOutUpOnLeaveAnimation({ anchor: 'leave' }),
  ],
})
export class OptionComponent {
  icon = IconType;
  iconSize = IconSizes;
  position = IconPosition;
  showPrice$ = new BehaviorSubject<boolean>(false);
  reveal$ = new BehaviorSubject<boolean>(false);
  E2e = Option;

  @HostBinding('class') hostClass = 'section-margin option-host';

  @Input() e2eId: string | null = null;

  /** Responsible for rendering the title on the option component */
  @Input() title!: string;

  /** Resposible for providing a title id. If none provided, a guid id will be auto generated  */
  @Input() titleId!: string;

  /** Responsible for rendering the lead text on the option component */
  @Input() leadText!: string;

  /** Resposible for providing a lead text id. If none provided, a guid id will be auto generated  */
  @Input() leadTextId!: string;

  /** Responsible for rendering the option detail label on the option component */
  @Input() optionDetailLabel!: string;

  /** Responsible for rendering the current currency code for the price */
  @Input() currencyCode!: string;

  /** Responsible for rendering the option detail price on the option component */
  @Input() optionDetailPrice!: number;

  /** Responsible for adding a read more that works with the reveal functionality */
  @Input() readMoreText!: string;

  /** Responsible for enabling the rendering of reveal content */
  @Input() set reveal(value: boolean) {
    this.reveal$.next(value);
  }

  /** Responsible for rendering the reveal title */
  @Input() revealTitle!: string;

  /** Responsible for rendering the reveal content */
  @Input() revealContentHtml!: string;

  /** Event responsible for emitting that the removed button has been clicked */
  @Output() optionRemoved = new EventEmitter();

  /** Event responsible for emitting when read more is clicked. Useful to change read more text */
  @Output() readMoreClicked = new EventEmitter<boolean>();

  @Input() set optionShowPrice(value: boolean) {
    this.showPrice$.next(value);
  }

  /** The click event that toggles the option details (with the price) */
  onRemovedOption(value: any): void {
    this.showPrice$.next(false);
    this.optionRemoved.emit(value);
  }

  toggleReveal() {
    const next = !this.reveal$.value;
    this.reveal$.next(next);
    this.readMoreClicked.emit(next);
  }
}
